import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';
import type { Profile, TranslateFn } from '../../types';
import { istFirmenZugang, darf } from '../../lib/zugang.js';

/** Einzelner Nav-Tab (aus dem Nav-Konfiguration-Array). */
export interface NavTab {
  id: string;
  label: string;
  icon: string;
  /** Wenn `true`, wird der Eintrag als Trennlinie gerendert (kein Tab). */
  divider?: boolean;
  /** Icon-Name für Divider-Sektionen (nur wenn `divider: true`). */
  divIcon?: string;
  /** Zeigt ein Lock-Icon an (z.B. gesperrte Admin-Bereiche). */
  locked?: boolean;
}

interface RenderBottomNavProps {
  user: Profile | null | undefined;
  /** Aktive Tab-ID. */
  tab: string;
  nav: (tabId: string) => void;
  navTabs: NavTab[];
  /** Ungelesen-Zähler pro Tab-ID (z.B. `{ chat: 3, admin-users: 1 }`). */
  navBadges: Record<string, number>;
  bottomSheet: boolean;
  setBottomSheet: Dispatch<SetStateAction<boolean>>;
  t: TranslateFn;
}

/** Aktiv gerenderter Bereich der Fahrer-Nav. */
type FahrerSection = 'sp' | 'mp' | 'konto';

/**
 * renderBottomNav — Mobile Bottom-Navigation mit Tab-Leiste und
 * Quick-Access-Sheet.
 *
 * Für Fahrer zeigt eine zusätzliche Section-Indicator-Reihe (Mein Geschäft /
 * Marktplatz / Konto). Die Quick-Tabs selbst variieren je nach Section und
 * Rolle. Der "Mehr"-Button öffnet das `BottomSheet` mit allen weiteren Tabs.
 */
export function renderBottomNav({
  user,
  tab,
  nav,
  navTabs,
  navBadges,
  bottomSheet,
  setBottomSheet,
  t,
}: RenderBottomNavProps) {
  if (!user) return null;

  const mpTabs = ['marktplatz', 'meine', 'meineauftraege', 'zertifikate', 'auszahlung'];
  const kontoTabs = ['profil', 'bewertungen', 'abo'];
  let currentSection: FahrerSection = 'sp';
  if (user.rolle === 'fahrer') {
    if (mpTabs.indexOf(tab) !== -1) currentSection = 'mp';
    else if (kontoTabs.indexOf(tab) !== -1 || tab === 'roteListe' || tab === 'ueberuns') currentSection = 'konto';
    else currentSection = 'sp';
  }

  let quickIds: string[];
  if (user.rolle === 'fahrer') {
    if (currentSection === 'mp') quickIds = ['marktplatz', 'meine', 'meineauftraege', 'auszahlung'];
    else if (currentSection === 'konto') quickIds = ['profil', 'bewertungen', 'abo', 'dashboard'];
    else quickIds = ['dashboard', 'eigeneauftraege', 'fahrtenbuch', 'finanzen'];
  } else if (istFirmenZugang(user)) {
    // Mitarbeiter ohne „Abrechnung sehen" (Migration 141): Adressbuch statt Abrechnung
    quickIds = ['dashboard', 'auftraege', darf(user, 'abrechnung') ? 'abrechnung' : 'adressbuch', 'profil'];
  } else {
    quickIds = navTabs
      .filter(function (nt) {
        return !nt.divider;
      })
      .slice(0, 4)
      .map(function (nt) {
        return nt.id;
      });
  }

  const quickTabs: NavTab[] = quickIds
    .map(function (id) {
      return navTabs.find(function (nt) {
        return nt.id === id;
      });
    })
    .filter(function (nt): nt is NavTab {
      return !!nt;
    });
  const sectionColor = 'var(--accent)';

  const sections: Array<[FahrerSection, string, string]> = [
    ['sp', 'briefcase', t('hcMeinGeschaeft')],
    ['mp', 'building', t('drpxMarktplatz')],
    ['konto', 'user', t('konto')],
  ];

  return (
    <nav
      className="mobile-bottom-nav"
      style={{
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        display: 'none',
        zIndex: 102,
        boxShadow: '0 -4px 20px rgba(0,0,0,.12)',
        backdropFilter: 'blur(12px)',
      }}>
      <div>
        {/* Section indicator (Fahrer only) */}
        {user.rolle === 'fahrer' &&
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              padding: '5px 8px 2px',
              borderBottom: '1px solid var(--border)',
            }}>
            {sections.map(function (sec) {
              const [secKey, secIcon, secLabel] = sec;
              return (
                <button
                  key={secKey}
                  onClick={function () {
                    const firstTab = secKey === 'mp' ? 'marktplatz' : secKey === 'konto' ? 'profil' : 'dashboard';
                    nav(firstTab);
                    setBottomSheet(false);
                  }}
                  style={{
                    ...(S.row as CSSProperties),
                    gap: 3,
                    padding: '4px 10px',
                    borderTop: 'none',
                    borderRight: 'none',
                    borderLeft: 'none',
                    borderBottom: currentSection === secKey ? '2px solid var(--accent)' : '2px solid transparent',
                    background: 'transparent',
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: 9,
                    fontWeight: currentSection === secKey ? 700 : 400,
                    color: currentSection === secKey ? 'var(--accent)' : 'var(--muted)',
                    cursor: 'pointer',
                    letterSpacing: 0.3,
                  }}>
                  <Icon
                    name={secIcon}
                    size={9}
                    color={currentSection === secKey ? 'var(--accent)' : 'var(--muted)'} />
                  {' '}
                  {secLabel}
                </button>
              );
            })}
          </div>}
        {/* Quick tabs */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'stretch',
            padding: '4px 4px env(safe-area-inset-bottom, 4px)',
          }}>
          {quickTabs.map(function (nt) {
            return (
              <button
                key={nt.id}
                onClick={function () {
                  nav(nt.id);
                  setBottomSheet(false);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  border: 'none',
                  background: tab === nt.id ? 'rgba(74,222,128,.1)' : 'transparent',
                  color: tab === nt.id ? sectionColor : 'var(--muted)',
                  cursor: 'pointer',
                  padding: '8px 6px',
                  fontSize: 10,
                  fontWeight: tab === nt.id ? 700 : 500,
                  fontFamily: "'DM Sans',sans-serif",
                  flex: 1,
                  borderRadius: 10,
                  transition: 'all .15s',
                  position: 'relative' as const,
                }}>
                {tab === nt.id &&
                  <div
                    style={{
                      position: 'absolute' as const,
                      top: -4,
                      width: 20,
                      height: 3,
                      borderRadius: 2,
                      background: sectionColor,
                    }} />}
                <span
                  style={{
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative' as const,
                  }}>
                  <Icon name={nt.icon} size={20} />
                  {navBadges[nt.id] > 0 &&
                    <span
                      style={{
                        position: 'absolute' as const,
                        top: -4,
                        right: -8,
                        background: nt.id === 'admin-users' ? '#f59e0b' : '#ef4444',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 14,
                        height: 14,
                        fontSize: 8,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {navBadges[nt.id] > 9 ? '9+' : navBadges[nt.id]}
                    </span>}
                </span>
                <span style={{ lineHeight: 1.1 }}>
                  {nt.label}
                </span>
              </button>
            );
          })}
          {/* "Mehr" button */}
          <button
            onClick={function () {
              setBottomSheet(function (s) {
                return !s;
              });
            }}
            style={{
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              border: 'none',
              background: bottomSheet ? 'rgba(74,222,128,.1)' : 'transparent',
              color: bottomSheet ? 'var(--accent)' : 'var(--muted)',
              cursor: 'pointer',
              padding: '8px 6px',
              fontSize: 10,
              fontWeight: 500,
              fontFamily: "'DM Sans',sans-serif",
              flex: 1,
              borderRadius: 10,
            }}>
            <span
              style={{ lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon
                name="menu"
                size={20}
                color={bottomSheet ? 'var(--accent)' : 'var(--muted)'} />
            </span>
            <span style={{ lineHeight: 1.1 }}>
              {t('mehr') || 'Mehr'}
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
}
